import { Decoder, array, nullable, number, object, string } from "decoders";
import { mean } from "ramda";
import { SiteStats } from "./dashboardData";
import { RainfallData, rainfallDataDecoder } from "./rainfallData";
import { WellLevelRecord, wellLevelRecordDecoder } from "./scada/wellLevel";
import { SiteData, siteDataDecoder } from "./site";
import { WellLevelNote, wellLevelNoteDecoder } from "./wellLevelNotes";

export interface WellLevelsRecord {
  date: string;
  rainfall: number | null;
  ns2: number | null;
  ns3: number | null;
  ns4: number | null;
  pc2: number | null;
  pc3: number | null;
  pc4: number | null;
  pc5: number | null;
}

export const wellLevelsRecordDecoder: Decoder<WellLevelsRecord> = object({
  date: string,
  rainfall: nullable(number),
  ns2: nullable(number),
  ns3: nullable(number),
  ns4: nullable(number),
  pc2: nullable(number),
  pc3: nullable(number),
  pc4: nullable(number),
  pc5: nullable(number),
});

export interface WellLevelsData {
  records: WellLevelsRecord[];
  scadaRecords: WellLevelRecord[];
  wellLevelNotes: WellLevelNote[];
  rainfall: RainfallData[];
  coordinates: SiteData[];
}

export const wellLevelsDataDecoder: Decoder<WellLevelsData> = object({
  records: array(wellLevelsRecordDecoder),
  scadaRecords: array(wellLevelRecordDecoder),
  wellLevelNotes: array(wellLevelNoteDecoder),
  rainfall: array(rainfallDataDecoder),
  coordinates: array(siteDataDecoder),
});

export const wellLevelsSitesReference = {
  ns2: { name: "GWWell-NS2", label: "NS-2" },
  ns3: { name: "GWWell-NS3", label: "NS-3" },
  ns4: { name: "GWWell-NS4", label: "NS-4" },
  pc2: { name: "GWWell-PC2", label: "PC-2" },
  pc3: { name: "GWWell-PC3", label: "PC-3" },
  pc4: { name: "GWWell-PC4", label: "PC-4" },
  pc5: { name: "GWWell-PC5", label: "PC-5" },
};

export function getWellLevelsSitesStats(sites: string[], data: WellLevelsData): SiteStats[] {
  return sites.map((site) => {
    const siteRecords = [
      ...(data.records
        .map((record) => record[site as keyof typeof wellLevelsSitesReference])
        .filter((x) => x !== null) as number[]),
      ...data.scadaRecords
        .filter((record) => record.wellName.toLowerCase() === site)
        .map((record) => record.waterLevelFT),
    ];

    if (siteRecords.length === 0) {
      return { siteMean: 0, siteMin: 0, siteMax: 0, siteName: site, samples: 0 };
    }

    const siteMean = mean(siteRecords);
    const siteMin = Math.min(...siteRecords);
    const siteMax = Math.max(...siteRecords);
    const siteName = site;

    return { siteMean, siteMin, siteMax, siteName, samples: siteRecords.length };
  });
}
